// SpeedMeister - Visual Performance Monitoring Tool
// A browser-based performance overlay for non-technical users

export interface PerformanceEntry {
  id: string;
  pageType: string;
  step: string;
  startTime: number;
  duration: number;
  details?: string;
  level: 'page' | 'component' | 'operation';
  status: 'success' | 'warning' | 'error';
}

export interface PerformanceSummary {
  pageType: string;
  totalDuration: number;
  entryCount: number;
  slowestOperation: PerformanceEntry | null;
  startTime: number;
  endTime: number;
}

class SpeedMeisterStore {
  private entries: PerformanceEntry[] = [];
  private listeners: Set<(entries: PerformanceEntry[]) => void> = new Set();
  private isEnabled: boolean = false;
  private sessionStartTime: number = 0;

  constructor() {
    // Check if SpeedMeister is enabled via localStorage only
    // Remove URL param check to keep it more hidden
    if (typeof window !== 'undefined') {
      this.isEnabled = localStorage.getItem('speedmeister') === 'true';
      this.sessionStartTime = performance.now();
    }
  }

  enable() {
    this.isEnabled = true;
    if (typeof window !== 'undefined') {
      localStorage.setItem('speedmeister', 'true');
      // Defer notification to next tick to avoid render issues
      setTimeout(() => this.notifyListeners(), 0);
    }
  }

  disable() {
    this.isEnabled = false;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('speedmeister');
    }
    this.clear();
  }

  isActive(): boolean {
    return this.isEnabled;
  }

  addEntry(entry: Omit<PerformanceEntry, 'id'>) {
    // Always record entries in memory (limited to last 100 to prevent memory issues)
    const fullEntry: PerformanceEntry = {
      ...entry,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };

    this.entries.push(fullEntry);
    
    // Keep only last 50 entries to prevent memory growth and duplicates
    if (this.entries.length > 50) {
      this.entries = this.entries.slice(-50);
    }

    // Only notify listeners if enabled
    if (this.isEnabled) {
      this.notifyListeners();
    }
  }

  getEntries(): PerformanceEntry[] {
    return [...this.entries];
  }

  getSummary(pageType?: string): PerformanceSummary {
    const filtered = pageType 
      ? this.entries.filter(e => e.pageType === pageType)
      : this.entries;

    if (filtered.length === 0) {
      return {
        pageType: pageType || 'all',
        totalDuration: 0,
        entryCount: 0,
        slowestOperation: null,
        startTime: this.sessionStartTime,
        endTime: performance.now()
      };
    }

    // Calculate total duration as sum of all operation durations
    // This gives us the actual time spent on operations, not wall clock time
    const totalDuration = filtered.reduce((sum, entry) => sum + entry.duration, 0);
    
    const startTime = Math.min(...filtered.map(e => e.startTime));
    const endTime = Math.max(...filtered.map(e => e.startTime + e.duration));
    const slowestOperation = filtered.reduce((prev, curr) => 
      curr.duration > (prev?.duration || 0) ? curr : prev
    , filtered[0]);

    return {
      pageType: pageType || 'all',
      totalDuration,
      entryCount: filtered.length,
      slowestOperation,
      startTime,
      endTime
    };
  }

  clear() {
    this.entries = [];
    this.notifyListeners();
  }

  subscribe(listener: (entries: PerformanceEntry[]) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners() {
    // Defer notifications to avoid setState during render
    if (typeof window !== 'undefined') {
      Promise.resolve().then(() => {
        this.listeners.forEach(listener => listener(this.getEntries()));
      });
    }
  }
}

// Global singleton
export const speedMeister = new SpeedMeisterStore();

// Integration with existing debug-performance.ts
export function recordPerformance(
  pageType: string,
  step: string,
  duration: number,
  details?: string
) {
  // Always record performance data (removed the isActive check)
  let level: PerformanceEntry['level'] = 'operation';
  if (step.includes('PAGE')) level = 'page';
  else if (step.includes('COMPONENT')) level = 'component';

  let status: PerformanceEntry['status'] = 'success';
  if (duration > 100) status = 'warning';
  if (duration > 500) status = 'error';

  speedMeister.addEntry({
    pageType,
    step,
    startTime: performance.now() - duration,
    duration,
    details,
    level,
    status
  });
}

// Utility to format duration for display
export function formatDuration(ms: number): string {
  if (ms < 1) return `${(ms * 1000).toFixed(0)}μs`;
  if (ms < 1000) return `${ms.toFixed(1)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

// Get performance color based on duration
export function getPerformanceColor(ms: number): string {
  if (ms < 16) return '#10b981'; // Green - Excellent (under 1 frame)
  if (ms < 50) return '#22c55e'; // Light Green - Good
  if (ms < 100) return '#84cc16'; // Lime - Acceptable
  if (ms < 300) return '#eab308'; // Yellow - Needs attention
  if (ms < 1000) return '#f97316'; // Orange - Poor
  return '#ef4444'; // Red - Critical
}